import { message } from 'antd'
import {
  FfsFormDataState,
  FfsReportSectionId,
} from 'pages/reporting/reports/ffs/ffsReportData'
import { IndicationWithFfsReport } from 'pages/reporting/reports/ffs/types'
import { getRequiredSections, Report } from 'pages/reporting/shared/types'
import { useCallback, useMemo, useState } from 'react'

interface SectionStatus {
  isComplete: boolean
  completedFields: number
  totalFields: number
}

/**
 * Get completion status for FFS report sections
 */
const getFfsReportCompletionStatus = (
  formData: FfsFormDataState,
  indicationsWithReports: IndicationWithFfsReport[]
): Record<string, SectionStatus> => {
  const status: Record<string, SectionStatus> = {}

  // Cover section (required)
  const coverFields = ['reportTitle', 'preparedBy', 'preparedFor']
  const coverData = {
    reportTitle: formData.coverSectionData?.reportTitle,
    preparedBy: formData.coverSectionData?.preparedBy,
    preparedFor: formData.coverSectionData?.preparedFor,
  }
  const coverComplete = coverFields.every(
    (field) => coverData[field as keyof typeof coverData]
  )
  status[FfsReportSectionId.COVER] = {
    isComplete: coverComplete,
    completedFields: coverFields.filter(
      (field) => coverData[field as keyof typeof coverData]
    ).length,
    totalFields: coverFields.length,
  }

  // AutoCAD Drawing section (optional)
  const hasDrawing = formData.autocadDrawingData?.hasDrawing ?? false
  const drawingComplete =
    !hasDrawing || !!formData.autocadDrawingData?.drawingPath
  status[FfsReportSectionId.AUTOCAD_DRAWING] = {
    isComplete: drawingComplete,
    completedFields: drawingComplete ? 1 : 0,
    totalFields: 1,
  }

  // Assessment Results section (required - one per indication)
  const indicationCount = indicationsWithReports.length
  const assessmentCount = formData.assessmentResults?.length ?? 0
  status[FfsReportSectionId.ASSESSMENT_RESULTS] = {
    isComplete: assessmentCount >= indicationCount && indicationCount > 0,
    completedFields: assessmentCount,
    totalFields: indicationCount,
  }

  // Review section
  const hasSignoff =
    !!formData.signoff &&
    (!!formData.signoff.supervisorSignoff || !!formData.signoff.reviewerSignoff)
  status[FfsReportSectionId.REVIEW] = {
    isComplete: hasSignoff,
    completedFields: hasSignoff ? 1 : 0,
    totalFields: 1,
  }

  // PDF Generation section (always complete)
  status[FfsReportSectionId.PDF_GENERATION] = {
    isComplete: true,
    completedFields: 1,
    totalFields: 1,
  }

  return status
}

interface UseReportNavigationResult {
  activePage: string | null
  navigateToPage: (page: string) => void
  returnToMenu: () => void
  canNavigate: (page: string) => boolean
  completionStatus: Record<string, SectionStatus>
}

interface UseReportNavigationProps {
  formData: FfsFormDataState
  report: Report
  indicationsWithReports: IndicationWithFfsReport[]
}

/**
 * Hook to manage report navigation and page completion status for FFS reports
 */
export const useFfsReportNavigation = ({
  formData,
  report,
  indicationsWithReports,
}: UseReportNavigationProps): UseReportNavigationResult => {
  const [activePage, setActivePage] = useState<string | null>(null)

  // Calculate completion status for all sections
  const completionStatus = useMemo(
    () => getFfsReportCompletionStatus(formData, indicationsWithReports),
    [formData, indicationsWithReports]
  )

  /**
   * Check if navigation to a page is allowed based on dependencies
   */
  const canNavigate = useCallback(
    (page: string): boolean => {
      // For review page, check if all required sections are complete
      if (page === 'review') {
        const requiredSections: string[] = getRequiredSections(report)
        const hasIncomplete = requiredSections.some(
          (section) => !completionStatus[section]?.isComplete
        )

        if (hasIncomplete) {
          message.warning('Please complete all required sections before review')
        }
      }

      // For PDF generation, check if review is complete
      if (page === 'pdfGeneration' && !completionStatus.review?.isComplete) {
        message.warning(
          'Please complete the review section before generating PDF'
        )
      }

      return true
    },
    [completionStatus, report]
  )

  /**
   * Navigate to a specific page
   */
  const navigateToPage = useCallback(
    (page: string) => {
      if (canNavigate(page)) {
        setActivePage(page)
      }
    },
    [canNavigate]
  )

  /**
   * Return to the main menu
   */
  const returnToMenu = useCallback(() => {
    setActivePage(null)
  }, [])

  return {
    activePage,
    navigateToPage,
    returnToMenu,
    canNavigate,
    completionStatus,
  }
}
